<script setup lang="ts">
import { computed } from 'vue';

import { CustomFieldType, toKebabCase } from '@nova/core';
import { useCustomForms } from '@satellite/composables/useCustomForms';
import CustomFieldLabel from './CustomFieldLabel.vue';

import type { ICustomField, IWarehouse } from '@nova/core';

/**
 * Renders a list of custom fields: the label row and the field component for each type.
 *
 * This is presentation only. It deliberately does not know where the fields came from, when they
 * are saved, or what payload a caller builds from them — those differ between the legacy
 * warehouse-template model and Custom Forms, and between a form that submits itself and one
 * whose parent submits.
 *
 * A field carries its own value in this model, so an edit is written straight onto the field,
 * which is what every caller's `v-model="field.value"` already did. `change` is raised after,
 * for callers that need to react rather than watch.
 *
 * The storage context for document fields stays with the caller: only the caller knows whether
 * uploads go to the authenticated endpoint or a public one.
 */
const {
  fields,
  warehouse = null,
  timezone,
  militaryTimeEnabled = false,
  displayLabels = true,
  listClass = [],
  itemClass = []
} = defineProps<{
  fields: ICustomField[];
  warehouse?: IWarehouse | null;
  /** Falls back to the warehouse's own timezone when not given. */
  timezone?: string;
  militaryTimeEnabled?: boolean;
  /** Turn off when the caller draws its own labels above each field. */
  displayLabels?: boolean;
  /** Added to the list's own layout classes rather than replacing them. */
  listClass?: string | string[];
  /** Added to each row's own layout classes rather than replacing them. */
  itemClass?: string | string[];
}>();

const emit = defineEmits<{
  change: [field: ICustomField];
  /** Raised by document fields while an upload is in flight, so a parent can hold its submit. */
  'toggle-confirm-button': [uploading: boolean];
}>();

const { customFormsComponentsMapping } = useCustomForms();

const resolvedTimezone = computed(() => timezone ?? warehouse?.timezone);

/** These field types already put the kebab-cased label on an element of their own. */
const FIELD_TYPES_WITH_OWN_TEST_ID = new Set<CustomFieldType>([
  CustomFieldType.BigString,
  CustomFieldType.DropDownMultiSelect,
  CustomFieldType.Timestamp
]);

/**
 * E2E locates a field by its kebab-cased label, which CustomFieldsOld put on each field. It goes on
 * the row rather than the field: through attribute fallthrough it would replace the
 * `<label>-custom-field-input` id that String, Number and Email set on their own root, which the
 * edit dialog and Custom Forms specs locate by. Types that already carry the label id are skipped,
 * so a locator by it never resolves to two elements.
 * This is a temporary workaround and will be removed in the PR that follows.
 */
function rowTestId(field: ICustomField) {
  return FIELD_TYPES_WITH_OWN_TEST_ID.has(field.type) ? undefined : toKebabCase(field.label);
}

function handleInput(field: ICustomField, value: ICustomField['value']) {
  field.value = value;
  emit('change', field);
}
</script>

<template>
  <div class="custom-fields-list" :class="['d-flex flex-column ga-5', listClass]">
    <!--
      Keyed with the index alongside the name: a flow can be attached to more than one trigger,
      so the same form field id can legitimately appear twice in one list.
    -->
    <div
      v-for="(field, index) in fields"
      :key="`${field.name}-${index}`"
      :data-testid="rowTestId(field)"
      :class="['d-flex flex-column ga-2', itemClass]">
      <custom-field-label v-if="displayLabels && field.label" :field="field" />

      <component
        :is="customFormsComponentsMapping[field.type]?.component"
        :display-label="false"
        :field="field"
        :military-time-enabled="militaryTimeEnabled"
        :model-value="field.value"
        :timezone="resolvedTimezone"
        :warehouse="warehouse"
        @toggle-confirm-button="emit('toggle-confirm-button', $event)"
        @update:model-value="handleInput(field, $event)" />
    </div>
  </div>
</template>
